import { createSlice } from '@reduxjs/toolkit';

import { normalizeStore, normalizePageElements } from '@editor2/services/storeService';
import { filterAvailableTypes } from '@editor2/services/configService';
import {
	fetchCourse,
	fetchCourseResourceSubtree,
	fetchNoConflictingResourceDiff,
	fetchPossiblyConflictingResourceDiff,
	fetchPageWithElementsData,
	saveResourceChanges,
	updatePageNumbers,
	generateNewGlossaryPage,
	fetchAllPagesWithElementsData,
	fetchCommits,
	updateResourceOnPushNotification,
	fetchResourceDiffOnPushNotification,
	fetchPendingDiff,
	generateMCQPQuestions
} from '@editor2/reducers/editor/effects';
import {
	addPageToPageGroupHelper,
	addPageToChapterHelper,
	movePageHelper,
	deletePageFromPageGroupHelper,
	deletePageFromChapterHelper,
	updatePageHelper,
	repaginateElementsHelper
} from './adapters/pagesAdapter';

import {
	addPageGroupToChapterHelper,
	movePageGroupHelper,
	deletePageGroupFromChapterHelper,
	updatePageGroupHelper
} from './adapters/pageGroupsAdapter';

import {
	addChapterHelper,
	moveChapterHelper,
	deleteChapterHelper,
	updateChapterHelper
} from './adapters/chaptersAdapter';

import {
	addElementHelper,
	moveElementHelper,
	deleteElementHelper,
	updateElementHelper
} from './adapters/elementsAdapters';

import { updateEditorFormHelper, updateYamlConfigHelper } from './adapters/bookAdapter';
import {
	addLectureSlideDecksHelper,
	updateLectureSlideDecksHelper,
	deleteLectureSlideDecksHelper,
	reorderLectureSlideDecksHelper
} from './adapters/lectureSlideDecksAdapter';
import { type CourseState } from '@editor2/types/courseState';
import { CollapseState } from '@editor2/types/collapseState';
import { applyNoConflictingDiff } from './adapters/applyDiffAdapter';
import { acceptIncoming } from './conflictResolution/acceptIncoming';
import { acceptCurrent } from './conflictResolution/acceptCurrent';
import { acceptBoth } from './conflictResolution/acceptBoth';
import { applyPossiblyConflictingDiff } from './conflictDetection/applyConflictingDiffAdapter';
import { applyPendingDiffHelper } from './adapters/pendingDiffAdapter';
import { recalculateConflictsHelper } from './adapters/recalculateConflictsAdapter';
import { courseToCourseProperties } from '@editor2/mappers/course';
import { clearTempFieldsHelper } from './adapters/clearTempFieldsAdapter';
import {
	type MCChoice,
	type MCQuestion,
	type MCQuestionPool,
	type EocCheckinChatbot
} from '@editor2/types/pageElements';
import { isEqual } from 'lodash-es';

const initChanges = {
	resourceConfig: [],
	toc: [],
	chapters: {},
	page_groups: {},
	pages: {},
	elements: {},
	lecture_slide_decks: {}
};

// Checks if an MCQuestion has no body and that any choices have no body and no rejoinder
const isPlaceholderMCQ = (q: MCQuestion) =>
	!q.body && Array.isArray(q.choices) && q.choices.every((c: MCChoice) => !c.body && !c.rejoinder);

export interface EditorState {
	[key: string]: CourseState;
}

const initialState: EditorState = {};

const tableOfContentsSlice = createSlice({
	name: 'tableOfContents',
	initialState,
	reducers: {
		addPageToPageGroup: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = addPageToPageGroupHelper(state[courseId], payload);
		},
		addPageToChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = addPageToChapterHelper(state[courseId], payload);
		},
		deletePageFromPageGroup: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = deletePageFromPageGroupHelper(state[courseId], payload);
		},
		deletePageFromChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = deletePageFromChapterHelper(state[courseId], payload);
		},
		addPageGroupToChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = addPageGroupToChapterHelper(state[courseId], payload);
		},
		deletePageGroupFromChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = deletePageGroupFromChapterHelper(state[courseId], payload);
		},
		addChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = addChapterHelper(state[courseId], payload);
		},
		deleteChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = deleteChapterHelper(state[courseId], payload);
		},
		addElementToPage: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = addElementHelper(state[courseId], payload);
		},
		deleteElementFromPage: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = deleteElementHelper(state[courseId], payload);
		},
		moveElement: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = moveElementHelper(state[courseId], payload);
		},
		movePage: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = movePageHelper(state[courseId], payload);
		},
		movePageGroup: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = movePageGroupHelper(state[courseId], payload);
		},
		moveChapter: (state, action) => {
			const { courseId, ...payload } = action.payload;
			state[courseId] = moveChapterHelper(state[courseId], payload);
		},
		updateYamlConfig(state, action) {
			const { yamlConfig, courseId } = action.payload;
			state[courseId] = updateYamlConfigHelper(state[courseId], { yamlConfig });
		},
		updateCourseResource(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = updateEditorFormHelper(state[courseId], payload);
		},
		addLectureSlideDeck(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = addLectureSlideDecksHelper(state[courseId], payload);
		},
		updateLectureSlideDeck(state, action) {
			const { courseId, lectureSlideDeckFamilyId, updates } = action.payload;
			state[courseId] = updateLectureSlideDecksHelper(state[courseId], {
				lectureSlideDeckFamilyId,
				updates
			});
		},
		deleteLectureSlideDeck(state, action) {
			const { courseId, lectureSlideDeckFamilyId } = action.payload;
			state[courseId] = deleteLectureSlideDecksHelper(state[courseId], {
				lectureSlideDeckFamilyId
			});
		},
		reorderLectureSlideDeck(state, action) {
			const { courseId, lectureSlideDeckFamilyId, index } = action.payload;
			state[courseId] = reorderLectureSlideDecksHelper(state[courseId], {
				lectureSlideDeckFamilyId,
				index
			});
		},
		incrementActiveFileUploads(state, action) {
			const { courseId } = action.payload;
			state[courseId].activeFileUploads = (state[courseId].activeFileUploads ?? 0) + 1;
		},
		decrementActiveFileUploads(state, action) {
			const { courseId } = action.payload;
			state[courseId].activeFileUploads = Math.max(0, (state[courseId].activeFileUploads ?? 0) - 1);
		},
		editChaptersCollapseState(state, action) {
			const { collapseState, courseId } = action.payload;
			state[courseId].collapseState.chapters = collapseState;
		},
		editPageGroupsCollapseState(state, action) {
			const { collapseState, courseId } = action.payload;
			state[courseId].collapseState.page_groups = collapseState;
		},
		editElementsCollapseState(state, action) {
			const { collapseState, courseId } = action.payload;
			state[courseId].collapseState.elements = collapseState;
		},
		updateChapter(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = updateChapterHelper(state[courseId], payload);
		},
		updatePageGroup(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = updatePageGroupHelper(state[courseId], payload);
		},
		updatePage(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = updatePageHelper(state[courseId], payload);
		},
		updateElement(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = updateElementHelper(state[courseId], payload);
		},
		repaginateElements(state, action) {
			const { courseId, ...payload } = action.payload;
			state[courseId] = repaginateElementsHelper(state[courseId], payload);
		},
		setSettingMode(state, action) {
			const { courseId, settingsMode } = action.payload;
			state[courseId].settingsMode = settingsMode;
		},
		updateActiveTab(state, action) {
			const { courseId, key, activeTab } = action.payload;
			state[courseId].activeTab[key] = activeTab;
		},
		updateActiveUsers(state, action) {
			const { courseId, room, activeUsers } = action.payload;
			if (!isEqual(state[courseId].activeUsers[room], activeUsers)) {
				state[courseId].activeUsers[room] = activeUsers;
			}
		},
		removeActiveUsers(state, action) {
			const { courseId, room } = action.payload;
			const { [room]: _, ...rooms } = state[courseId].activeUsers;
			state[courseId].activeUsers = rooms;
		},
		acceptCurrentChange(state, action) {
			const { courseId, conflict } = action.payload;
			state[courseId] = acceptCurrent(state[courseId], conflict);

			// when all conflicts resolved - apply pending diff if exists
			if (!state[courseId].conflicts.length) {
				if (state[courseId].pendingDiff) {
					state[courseId] = applyPendingDiffHelper(state[courseId]);
				} else {
					state[courseId] = clearTempFieldsHelper(state[courseId]);
				}
			}
		},
		acceptIncomingChange(state, action) {
			const { courseId, conflict } = action.payload;
			state[courseId] = acceptIncoming(state[courseId], conflict);

			// when all conflicts resolved - apply pending diff if exists
			if (!state[courseId].conflicts.length) {
				if (state[courseId].pendingDiff) {
					state[courseId] = applyPendingDiffHelper(state[courseId]);
				} else {
					state[courseId] = clearTempFieldsHelper(state[courseId]);
				}
			}
		},
		acceptBothChanges(state, action) {
			const { courseId, conflict } = action.payload;
			state[courseId] = acceptBoth(state[courseId], conflict);

			// when all conflicts resolved - apply pending diff if exists
			if (!state[courseId].conflicts.length) {
				if (state[courseId].pendingDiff) {
					state[courseId] = applyPendingDiffHelper(state[courseId]);
				} else {
					state[courseId] = clearTempFieldsHelper(state[courseId]);
				}
			}
		},
		skipQuestionGenerationFeedback(state, action) {
			const { courseId, elementId } = action.payload;
			(state[courseId].elements.entities[elementId] as MCQuestionPool).needsQuestionFeedback =
				false;
		},
		storeQuestionGenerationPayload(state, action) {
			const { courseId, elementId, questionGenerationPayload } = action.payload;
			(state[courseId].elements.entities[elementId] as MCQuestionPool).questionGenerationPayload =
				questionGenerationPayload;
		},
		storeQuestionGenerationResponse(state, action) {
			const { courseId, elementId, questionGenerationResponse } = action.payload;
			(state[courseId].elements.entities[elementId] as MCQuestionPool).questionGenerationResponse =
				questionGenerationResponse;
		},
		clearQuestionGenerationData(state, action) {
			const { courseId, elementId } = action.payload;
			(state[courseId].elements.entities[elementId] as MCQuestionPool).questionGenerationPayload =
				undefined;
			(state[courseId].elements.entities[elementId] as MCQuestionPool).questionGenerationResponse =
				undefined;
		},
		setEocConceptsGenerating(state, action) {
			const { courseId, elementId, isGenerating } = action.payload;
			// Transient, UI-only flag — does not count as an element change.
			(state[courseId].elements.entities[elementId] as EocCheckinChatbot).isGeneratingConcepts =
				isGenerating;
		},
		appendEocKeyConcepts(state, action) {
			const { courseId, elementId, concepts } = action.payload;
			const element = state[courseId].elements.entities[elementId] as EocCheckinChatbot;
			const existing = element.key_concepts ?? [];
			// Route through updateElementHelper so the append is tracked as an element
			// change (pending-changes view, save, conflict resolution).
			state[courseId] = updateElementHelper(state[courseId], {
				elementId,
				key_concepts: [...existing, ...concepts]
			});
		},
		setShowConflictsModal(state, action) {
			const { courseId, showModal } = action.payload;
			state[courseId].showConflictsModal = showModal;
		},
		updateCourseConfigAndHeadCommit(state, action) {
			const { courseId, course, config, policies, defaults, theme, headCommit } = action.payload;

			const { chapter_types, page_types, element_types, color_palettes, metadata } =
				filterAvailableTypes(config, defaults);

			state[courseId] = {
				...state[courseId],
				course: course.course,
				config: { chapter_types, page_types, element_types, color_palettes, metadata },
				policies,
				defaults,
				theme,
				headCommit: headCommit.commit
			};
		},
		recalculateConflicts(state, action) {
			const { courseId } = action.payload;

			state[courseId] = recalculateConflictsHelper(state[courseId]);
		}
	},

	extraReducers: (builder) => {
		builder
			.addCase(fetchCourse.fulfilled, (state, action) => {
				const courseId = action.meta.arg;
				const { course, config, policies, defaults, theme, headCommit } = action.payload;

				const { chapter_types, page_types, element_types, color_palettes, metadata } =
					filterAvailableTypes(config, defaults);

				state[courseId] = {
					...state[courseId],
					course: course.course,
					config: { chapter_types, page_types, element_types, color_palettes, metadata },
					policies,
					defaults,
					theme,
					isCourseLoaded: true,
					headCommit: headCommit.commit
				};
			})

			.addCase(fetchCourseResourceSubtree.pending, (state, action) => {
				const { courseId } = action.meta.arg;

				state[courseId] = {
					...state[courseId],
					isSubtreeLoaded: false
				};
			})
			.addCase(fetchCourseResourceSubtree.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { nodes } = action.payload;

				const { book, chapters, page_groups, pages, elements, lecture_slide_decks } =
					normalizeStore(nodes);

				const courseInit = { book, chapters, page_groups, pages, elements, lecture_slide_decks };

				state[courseId] = {
					...state[courseId],
					...courseInit,

					isSubtreeLoaded: true,
					isLoading: false,
					activeFileUploads: 0,

					conflicts: [],
					changes: initChanges,
					initialCourse: courseInit,
					activeUsers: {},
					collapseState: {
						chapters: CollapseState.ExpandAll,
						page_groups: CollapseState.ExpandAll,
						elements: CollapseState.CollapseAll
					},
					activeTab: {
						chapters: 'toc',
						page_groups: 'toc',
						elements: 'toc'
					}
				};
			})
			.addCase(fetchCourseResourceSubtree.rejected, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId] = {
					...state[courseId],
					isSubtreeLoaded: false
				};
			})

			.addCase(fetchNoConflictingResourceDiff.pending, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = true;
			})
			.addCase(fetchNoConflictingResourceDiff.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { nodes } = action.payload;

				state[courseId] = applyNoConflictingDiff(state[courseId], nodes);

				state[courseId] = {
					...state[courseId],
					isLoading: false,
					initialCourse: courseToCourseProperties(state[courseId]),
					changes: initChanges
				};
			})
			.addCase(fetchNoConflictingResourceDiff.rejected, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = false;
			})

			.addCase(fetchPossiblyConflictingResourceDiff.pending, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].tempInitCourse = { ...state[courseId].initialCourse };
				state[courseId].tempCurrentCourse = courseToCourseProperties(state[courseId]);
				state[courseId].isLoading = true;
			})
			.addCase(fetchPossiblyConflictingResourceDiff.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { nodes } = action.payload;

				state[courseId].diffNodes = nodes;
				state[courseId] = applyPossiblyConflictingDiff(state[courseId], nodes);

				state[courseId].diffNodes = state[courseId].conflicts.length ? nodes : [];

				state[courseId] = {
					...state[courseId],
					tempInitCourse: state[courseId].conflicts.length ? state[courseId].tempInitCourse : null,
					tempCurrentCourse: state[courseId].conflicts.length
						? state[courseId].tempCurrentCourse
						: null,
					isLoading: false
				};
			})
			.addCase(fetchPossiblyConflictingResourceDiff.rejected, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = false;
			})

			.addCase(fetchResourceDiffOnPushNotification.pending, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].tempInitCourse = { ...state[courseId].initialCourse };
				state[courseId].tempCurrentCourse = courseToCourseProperties(state[courseId]);
			})
			.addCase(fetchResourceDiffOnPushNotification.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { nodes } = action.payload;

				// before diff is applied
				state[courseId].diffNodes = nodes;
				state[courseId].isLoading = true;

				state[courseId] = applyPossiblyConflictingDiff(state[courseId], nodes);

				// after diff is applied
				state[courseId] = {
					...state[courseId],
					diffNodes: state[courseId].conflicts.length ? nodes : [],
					tempInitCourse: state[courseId].conflicts.length ? state[courseId].tempInitCourse : null,
					tempCurrentCourse: state[courseId].conflicts.length
						? state[courseId].tempCurrentCourse
						: null,
					isLoading: false
				};
			})
			.addCase(fetchResourceDiffOnPushNotification.rejected, (state, action) => {})

			.addCase(fetchPendingDiff.pending, (state, action) => {})
			.addCase(fetchPendingDiff.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { nodes, commits, headCommit } = action.payload;

				state[courseId].pendingDiff = { nodes, commits, headCommit };
			})
			.addCase(fetchPendingDiff.rejected, (state, action) => {})

			.addCase(fetchPageWithElementsData.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { nodes } = action.payload;

				const { page, elements } = normalizePageElements(nodes);

				state[courseId].pages.entities[page.family_id].elements = page.elements;
				state[courseId].initialCourse.pages.entities[page.family_id].elements = page.elements;

				state[courseId].elements.entities = {
					...state[courseId].elements.entities,
					...elements.entities
				};
				state[courseId].elements.ids = [...state[courseId].elements.ids, ...elements.ids];

				state[courseId].initialCourse.elements.entities = {
					...state[courseId].initialCourse.elements.entities,
					...elements.entities
				};
				state[courseId].initialCourse.elements.ids = [
					...state[courseId].initialCourse.elements.ids,
					...elements.ids
				];

				state[courseId].collapseState.elements = CollapseState.CollapseAll;
			})

			.addCase(fetchAllPagesWithElementsData.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { pages } = action.payload;

				pages.forEach(({ nodes }) => {
					const { page, elements } = normalizePageElements(nodes);

					state[courseId].pages.entities[page.family_id].elements = page.elements;
					state[courseId].initialCourse.pages.entities[page.family_id].elements = page.elements;

					state[courseId].elements.entities = {
						...state[courseId].elements.entities,
						...elements.entities
					};
					state[courseId].elements.ids = [...state[courseId].elements.ids, ...elements.ids];

					state[courseId].initialCourse.elements.entities = {
						...state[courseId].initialCourse.elements.entities,
						...elements.entities
					};
					state[courseId].initialCourse.elements.ids = [
						...state[courseId].initialCourse.elements.ids,
						...elements.ids
					];
				});
			})
			.addCase(fetchCommits.pending, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = true;
			})
			.addCase(fetchCommits.fulfilled, (state, action) => {
				const { courseId } = action.meta.arg;
				const { commits } = action.payload;

				state[courseId].commits = commits;
				state[courseId].isLoading = false;
			})
			.addCase(fetchCommits.rejected, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = false;
			})

			.addCase(saveResourceChanges.pending, (state, action) => {
				const { id: courseId } = action.meta.arg.payload.course;
				state[courseId].isLoading = true;
			})
			.addCase(saveResourceChanges.fulfilled, (state, action) => {
				const { id: courseId } = action.meta.arg.payload.course;
				state[courseId].settingsMode = false;
			})
			.addCase(saveResourceChanges.rejected, (state, action) => {
				const { id: courseId } = action.meta.arg.payload.course;
				state[courseId].isLoading = false;
			})

			.addCase(updateResourceOnPushNotification.pending, (state, action) => {})
			.addCase(updateResourceOnPushNotification.fulfilled, (state, action) => {})
			.addCase(updateResourceOnPushNotification.rejected, (state, action) => {})

			.addCase(updatePageNumbers.pending, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = true;
			})
			.addCase(updatePageNumbers.rejected, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = false;
			})
			.addCase(generateNewGlossaryPage.pending, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = true;
			})
			.addCase(generateNewGlossaryPage.rejected, (state, action) => {
				const { courseId } = action.meta.arg;
				state[courseId].isLoading = false;
			})
			.addCase(generateMCQPQuestions.pending, (state, action) => {
				const { courseId, elementId } = action.meta.arg;
				(state[courseId].elements.entities[elementId] as MCQuestionPool).isGeneratingQuestions =
					true;
				(state[courseId].elements.entities[elementId] as MCQuestionPool).needsQuestionFeedback =
					false;
			})
			.addCase(generateMCQPQuestions.fulfilled, (state, action) => {
				const { courseId, elementId } = action.meta.arg;
				const { questions } = action.payload;

				const existingQuestions =
					(state[courseId].elements.entities[elementId] as MCQuestionPool).questions ?? [];

				// Filter out placeholder questions
				const filteredQuestions = existingQuestions.filter((q) => !isPlaceholderMCQ(q));

				const updateMCQPPayload = {
					elementId,
					questions: [...filteredQuestions, ...questions]
				};

				(state[courseId].elements.entities[elementId] as MCQuestionPool).isGeneratingQuestions =
					false;
				(state[courseId].elements.entities[elementId] as MCQuestionPool).needsQuestionFeedback =
					true;
				state[courseId] = updateElementHelper(state[courseId], updateMCQPPayload);
			})
			.addCase(generateMCQPQuestions.rejected, (state, action) => {
				const { courseId, elementId } = action.meta.arg;
				(state[courseId].elements.entities[elementId] as MCQuestionPool).isGeneratingQuestions =
					false;
				(state[courseId].elements.entities[elementId] as MCQuestionPool).needsQuestionFeedback =
					false;
			});
	}
});

export const {
	addPageToPageGroup,
	addPageToChapter,
	deletePageFromPageGroup,
	deletePageFromChapter,
	addPageGroupToChapter,
	deletePageGroupFromChapter,
	addChapter,
	deleteChapter,
	addElementToPage,
	deleteElementFromPage,
	movePage,
	movePageGroup,
	moveChapter,
	moveElement,
	updateYamlConfig,
	updateCourseResource,
	addLectureSlideDeck,
	updateLectureSlideDeck,
	deleteLectureSlideDeck,
	reorderLectureSlideDeck,
	incrementActiveFileUploads,
	decrementActiveFileUploads,
	editChaptersCollapseState,
	editPageGroupsCollapseState,
	editElementsCollapseState,
	updateChapter,
	updatePageGroup,
	updatePage,
	updateElement,
	repaginateElements,
	setSettingMode,
	skipQuestionGenerationFeedback,
	storeQuestionGenerationPayload,
	storeQuestionGenerationResponse,
	clearQuestionGenerationData,
	setEocConceptsGenerating,
	appendEocKeyConcepts,
	updateActiveTab,
	updateActiveUsers,
	removeActiveUsers,
	acceptCurrentChange,
	acceptIncomingChange,
	acceptBothChanges,
	setShowConflictsModal,
	updateCourseConfigAndHeadCommit,
	recalculateConflicts
} = tableOfContentsSlice.actions;

export default tableOfContentsSlice.reducer;
